import React, { useEffect, useState, useCallback } from "react";
import "./Testimonials.css";
import { FaStar, FaQuoteLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import useInView from "../hooks/useInView";

/* ------------------------------------------------------------------
 *  TESTIMONIALS
 *  ------------------------------------------------------------------
 *  Each testimonial just needs a quote and a name. Replace these
 *  placeholder ones with real client feedback when you have it.
 *  The avatar circle auto-generates initials from the name.
 * ------------------------------------------------------------------ */
const TESTIMONIALS = [
  {
    quote:
      "Vezron delivered our quantity takeoffs ahead of schedule and saved us at least 20 hours per project. Their attention to detail is exceptional.",
    name: "Michael R.",
    color: "#4169e1",
    rating: 5,
  },
  {
    quote:
      "We've worked with several outsourcing partners. Vezron is the only one we've kept long-term — accuracy and turnaround are simply better.",
    name: "Priya S.",
    color: "#7c3aed",
    rating: 5,
  },
  {
    quote:
      "Our project timelines tightened significantly. The estimation work was thorough and the back-and-forth was kept to a minimum.",
    name: "James W.",
    color: "#0ea5e9",
    rating: 5,
  },
  {
    quote:
      "Professional, responsive, and reliable. Our CAD drafting work has never been smoother since we started with Vezron.",
    name: "Anita K.",
    color: "#10b981",
    rating: 5,
  },
];

const getInitials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [headRef, headInView] = useInView();

  const total = TESTIMONIALS.length;

  const goTo = useCallback((i) => {
    setIndex(((i % total) + total) % total);
  }, [total]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, paused]);

  return (
    <section className="testimonials-section">
      <div className="testimonials-bg-blob blob-a" />
      <div className="testimonials-bg-blob blob-b" />

      <div className="testimonials-container">
        <div
          ref={headRef}
          className={`testimonials-head ${headInView ? "in-view" : ""}`}
        >
          <span className="section-eyebrow">Testimonials</span>
          <h2>What our clients say</h2>
          <p>
            Real feedback from teams we've partnered with.
          </p>
        </div>

        <div
          className="testimonials-stage"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            className="t-nav t-nav-left"
            onClick={prev}
            aria-label="Previous testimonial"
          >
            <FaChevronLeft />
          </button>

          <div className="testimonial-card-wrap">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                className={`testimonial-card ${i === index ? "active" : ""}`}
                aria-hidden={i !== index}
              >
                <FaQuoteLeft className="quote-mark" />
                <p className="t-quote">"{t.quote}"</p>

                <div className="t-rating">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <FaStar key={k} />
                  ))}
                </div>

                <div className="t-person">
                  <div
                    className="t-avatar"
                    style={{ background: t.color }}
                    aria-hidden="true"
                  >
                    {getInitials(t.name)}
                  </div>
                  <div className="t-person-meta">
                    <strong>{t.name}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <button
            className="t-nav t-nav-right"
            onClick={next}
            aria-label="Next testimonial"
          >
            <FaChevronRight />
          </button>
        </div>

        <div className="t-dots">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              className={`t-dot ${i === index ? "active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
